import React from 'react';
import { DetailsWorkspaceModal, TableActionButton } from "@/components/ui";
import { useQuery } from '@tanstack/react-query';
import { leadUploadService } from '../../../client_contact_stages/verve-design/services/leadUploadService';
import { BASE_URL } from '@/lib/API';
import { FileText, User, Download, Eye, Loader2, File, Image as ImageIcon } from 'lucide-react';
import { fmtDate } from "@/lib/formatters";
import { showSuccessMessage } from "@/lib/toast";

export const LeadMediaViewer = ({ isOpen, onClose, lead }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lead-uploads', lead?._id],
    queryFn: async () => {
      if (!lead?._id) return null;
      const res = await leadUploadService.getFilesByLead(lead._id);
      return res.data?.data?.files || [];
    },
    enabled: isOpen && !!lead,
  });

  const files = data || [];
  const displayName = lead?.customerName || `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim() || "Lead";
  
  const fields = [
    { icon: User, label: "Verve ID", value: (lead?.customId || "").toUpperCase() },
    { icon: User, label: "Client Name", value: displayName },
    { icon: FileText, label: "Media Count", value: `${files.length} Files` }
  ];

  const handleDownload = (file) => {
    const downloadUrl = `${BASE_URL}/api/lead-uploads/stream/${file.fileId}?download=1`;
    window.location.href = downloadUrl;
    showSuccessMessage("File downloaded successfully!");
  };

  const handlePreview = (file) => {
    const previewUrl = `${BASE_URL}/api/lead-uploads/stream/${file.fileId}`;
    window.open(previewUrl, '_self');
  };

  const getFileIcon = (mimetype) => {
    if (mimetype?.includes('image')) return <ImageIcon className="w-5 h-5 text-blue-500" />;
    if (mimetype?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <DetailsWorkspaceModal
      isOpen={isOpen}
      onClose={onClose}
      title="VERVE KITCHENS"
      subtitle="UPLOADED MEDIA"
      fields={fields}
      hideSidebar={true}
      maxWidthClass="max-w-[1000px]"
      closeIcon="minimize"
    >
      <div className="flex flex-col gap-8 min-h-[300px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-full pt-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
          </div>
        ) : error ? (
          <div className="text-center text-red-500 p-8">Error loading media files.</div>
        ) : files.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full pt-12 text-slate-500">
            <File className="w-12 h-12 mb-3 text-slate-300" />
            <p>No media files uploaded yet.</p>
          </div>
        ) : (
          <div className="flex flex-col">
            <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-4 uppercase">
              Lead Documents & Media
            </h4>
            
            <div className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse table-fixed">
                  <thead>
                    <tr className="bg-slate-50/50 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <th className="w-[10%] px-4 py-3 border-b border-slate-200 font-semibold text-center">Type</th>
                      <th className="w-[30%] px-4 py-3 border-b border-slate-200 font-semibold">File Name</th>
                      <th className="w-[15%] px-4 py-3 border-b border-slate-200 font-semibold">Stage</th>
                      <th className="w-[10%] px-4 py-3 border-b border-slate-200 font-semibold">Size</th>
                      <th className="w-[15%] px-4 py-3 border-b border-slate-200 font-semibold">Uploaded By</th>
                      <th className="w-[10%] px-4 py-3 border-b border-slate-200 font-semibold">Date</th>
                      <th className="w-[10%] px-4 py-3 border-b border-slate-200 font-semibold text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-[12px] text-slate-700">
                    {files.map((file) => (
                      <tr key={file._id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3 text-center align-middle">
                          <div className="flex justify-center">
                            {getFileIcon(file.mimetype)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800 truncate" title={file.title || file.filename}>
                            {file.title || file.filename}
                          </p>
                          {file.title && <p className="text-[10px] text-slate-400 truncate mt-0.5">{file.filename}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded bg-indigo-50 text-indigo-700 text-[10px] font-bold uppercase tracking-wider">
                            {file.type?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-500">
                          {formatSize(file.size)}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {file.uploadedBy?.name || 'System'}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {fmtDate(file.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <TableActionButton 
                              icon={Eye} 
                              variant="primary" 
                              onClick={() => handlePreview(file)}
                              title="Preview File"
                            />
                            <TableActionButton 
                              icon={Download} 
                              variant="success" 
                              onClick={() => handleDownload(file)}
                              title="Download File"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </DetailsWorkspaceModal>
  );
};
